/** The list of tokens we support replacement for. These are just names/symbols to identify them. */
type Token = "providerName" | "firstName" | "amountDue" | "paymentLink";

/** Inputs to render a template: actual values for the tokens we replace. */
export type Inputs = Record<Token, string>;

/** A list of tokens we do not perform truncation on. */
const noTruncationTokens: Token[] = ["providerName", "paymentLink"];

/** Information needed for each token. */
interface Replacement {
    /** The token being replaced. */
    token: Token;

    /** The regular expression that matches the token. */
    match: RegExp;

    /** The maximum length we expect the token to be. */
    size: number;
}

/** Length of patient portal links generated by our system. */
export const PaymentLinkLength = 38;

/** Replacements that match a token and identify the maximum length we expect that token to be. */
const replacements: Replacement[] = [
    { token: "providerName", match: /\[\[Provider Name\]\]/gi, size: 20 },
    { token: "firstName", match: /\[\[First Name\]\]/gi, size: 15 },
    { token: "amountDue", match: /\[\[Amount Due\]\]/gi, size: 10 },
    // NOTE: The user will actually receive a URL of the form https://app.operadds.com/u/m/xxxxx which is about 36
    // characters, but Opera messaging will validate message length based on the raw URL we pass in! Therefore we have
    // to validate based on the length of our URLs of the form https://pay.vynetrellis.com/xxxxxxxx/t which is about 38
    // characters. If Opera changes their validation approach at some point, we can give the user some more space.
    { token: "paymentLink", match: /\[\[Payment Link\]\]/gi, size: PaymentLinkLength },
];

/**
 * Templated text message, used to ask a patient for payment.
 *
 * Port of Vyne.Payments.BusinessLogic.Models.PaymentTextMessage, but inverted: the template is the stored data and the
 * P/RP details are passed in, to aid in memoization. Additionally, we do not perform extensive validation here, because
 * we expect it to have already occurred server-side.
 */
export class PaymentTextMessage {
    template: string;

    constructor(tpl: string) {
        this.template = tpl;
    }

    /** Fill in the template using placeholder values that are the maximum expected length, for measurement. */
    getMaximumTextMessage(providerName?: string) {
        // TypeScript is bad here, maybe there's a better way to do this eventually that doesn't require casting
        const inputs = Object.fromEntries(
            replacements.map((repl) => [repl.token, ".".repeat(repl.size)] as const),
        ) as Inputs;

        // Use real provider name when available for maximum fidelity
        if (providerName) {
            inputs.providerName = providerName;
        }

        return this.getTextMessage(inputs);
    }

    /**
     * Get the text message to send to the Patient/Responsible Party by filling in template tokens.
     * @param inputs Replacement values for each of the tokens we can use.
     * @returns The SMS text message to send.
     */
    getTextMessage(inputs: Inputs) {
        let message = this.template;

        for (const repl of replacements) {
            let value = inputs[repl.token];
            if (!noTruncationTokens.includes(repl.token)) {
                value = value.substring(0, repl.size);
            }

            message = message.replaceAll(repl.match, value);
        }

        return message;
    }
}
